import { waitLabel, type Flag, type Note, type StudentCard, type SubmissionBrief } from "@/app/reviewQueries";
import { count, plural } from "@/lib/plural";
import type { Status } from "@/ui/StatusChip";

/** M3: подписи и производные карточки студента — из данных, не из строк макета. */

/** SLA ответа ментора, ч — открытый вопрос CLAUDE.md, дефолт макета (бэкенд считает `late` для очереди). */
export const SLA_HOURS = 24;

export const STATUS: Record<SubmissionBrief["status"], Status> = {
  on_review: "in_review",
  changes_requested: "needs_changes",
  accepted: "accepted",
};

const HOUR = 3_600_000;
export const hoursBetween = (fromIso: string, toMs: number) => Math.max(0, (toMs - new Date(fromIso).getTime()) / HOUR);

export const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
export const fmtDateTime = (iso: string) => new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** Колонка «ответ»: ждёт — сколько уже; есть вердикт — через сколько ответили. */
export function replyLabel(s: SubmissionBrief, now: number): string {
  if (s.verdict_at) return `через ${waitLabel(hoursBetween(s.submitted_at, new Date(s.verdict_at).getTime()))}`;
  return `ждёт ${waitLabel(hoursBetween(s.submitted_at, now))}`;
}

export const isPending = (s: SubmissionBrief) => s.status === "on_review";
export const isLate = (s: SubmissionBrief, now: number) => isPending(s) && hoursBetween(s.submitted_at, now) >= SLA_HOURS;

/** Сдачи — новые сверху. */
export const sortSubmissions = (subs: SubmissionBrief[]) => [...subs].sort((a, b) => b.submitted_at.localeCompare(a.submitted_at));
export const sortNotes = (notes: Note[]) => [...notes].sort((a, b) => b.created_at.localeCompare(a.created_at));

/** Среднее число итераций до принятия по принятым сдачам; null — ещё ничего не принято. */
export function meanIterations(subs: SubmissionBrief[]): number | null {
  const acc = subs.filter((s) => s.status === "accepted");
  if (acc.length === 0) return null;
  return acc.reduce((sum, s) => sum + s.iteration, 0) / acc.length;
}

export function submissionsHint(subs: SubmissionBrief[]): string {
  const n = count(subs.length, "отправка", "отправки", "отправок");
  const mean = meanIterations(subs);
  if (mean === null) return `${n} · принятых пока нет`;
  return `${n} · среднее ${mean.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${plural(Math.round(mean), "итерация", "итерации", "итераций")} до принятия`;
}

/** Чипы над сводкой — выводятся из сдач, а не пишутся строкой. */
export function flagChips(card: StudentCard, now: number): { label: string; tone: "err" | "warn" }[] {
  const out: { label: string; tone: "err" | "warn" }[] = [];
  const latest = sortSubmissions(card.submissions)[0];
  if (latest && isLate(latest, now)) out.push({ label: `сдача просрочена ${waitLabel(hoursBetween(latest.submitted_at, now))}`, tone: "err" });
  if (latest && latest.iteration >= 2) out.push({ label: `${latest.iteration}-я итерация`, tone: "warn" });
  return out;
}

const ORDINAL_M: Record<number, string> = { 2: "второй", 3: "третий", 4: "четвёртый", 5: "пятый", 6: "шестой", 7: "седьмой" };
const ordinalM = (n: number) => ORDINAL_M[n] ?? `${n}-й`;

/** Текст карточки риска (макет M3, адаптирован к count/reason флага). */
export function riskText(flag: Flag): string {
  return `${count(flag.count, "возврат", "возврата", "возвратов")} подряд по одной причине — «${flag.reason.label}». Это повод позвать на созвон, а не писать ${ordinalM(flag.count + 1)} текст.`;
}

export const repeatLabel = (n: number) => `${n}×`;
/** Три повтора — сигнал (порог эскалации); два — предупреждение. */
export const repeatTone = (n: number, threshold = 3): "err" | "warn" => (n >= threshold ? "err" : "warn");

export type Fact = { k: string; v: string; tone?: "ink2" | "warn" | "mut" };

export function facts(card: StudentCard): Fact[] {
  const subs = card.submissions;
  const accepted = subs.filter((s) => s.status === "accepted").length;
  const pending = subs.filter(isPending).length;
  const mean = meanIterations(subs);
  return [
    { k: "ментор", v: card.mentor?.name ?? "не назначен", tone: card.mentor ? "ink2" : "warn" },
    { k: "сдач всего", v: String(subs.length) },
    { k: "принято", v: String(accepted) },
    { k: "ждёт проверки", v: String(pending), tone: pending ? "warn" : "mut" },
    { k: "среднее итераций", v: mean === null ? "—" : mean.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), tone: mean !== null && mean >= 2 ? "warn" : "ink2" },
  ];
}
